import { useFeedback } from "@ark/ui/feedback";
import { useEffect } from "react";

import { shouldCheckForDesktopUpdates } from "./desktopUpdaterPolicy";
import {
  activateDesktopUpdatePrompt,
  setDesktopUpdatePromptActive,
  waitForMajorReleasePrompt,
} from "./productReleases";

const updateCheckDelayMs = 5_000;

export function DesktopUpdater() {
  const { message, modal } = useFeedback();

  useEffect(() => {
    if (!shouldCheckForDesktopUpdates()) return;

    let active = true;
    let installing = false;
    let destroyPrompt: (() => void) | undefined;
    let pendingUpdate: { close(): Promise<void> } | null = null;
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      void (async () => {
        await waitForMajorReleasePrompt(controller.signal);
        if (!active) return null;
        const { check } = await import("@tauri-apps/plugin-updater");
        const update = await check({ timeout: 15_000 });
        if (!update || !active) return update;
        const promptActive = await activateDesktopUpdatePrompt(
          controller.signal,
        );
        if (!promptActive || !active) {
          await update.close();
          return null;
        }
        return update;
      })()
        .then((update) => {
          if (!active) {
            void update?.close();
            return;
          }
          if (!update) return;
          pendingUpdate = update;
          const prompt = modal.confirm({
            afterClose: () => setDesktopUpdatePromptActive(false),
            centered: true,
            title: `发现 Ark ${update.version}`,
            content:
              update.body?.trim() ||
              `当前版本 ${update.currentVersion}，新版本已准备好。`,
            okText: "更新并重启",
            cancelText: "稍后",
            onCancel: () => {
              void update.close();
              pendingUpdate = null;
            },
            onOk: async () => {
              installing = true;
              const hide = message.loading("正在下载并安装更新", 0);
              try {
                await update.downloadAndInstall(undefined, {
                  timeout: 120_000,
                });
                const { relaunch } = await import("@tauri-apps/plugin-process");
                await relaunch();
              } catch {
                await update.close().catch(() => undefined);
                pendingUpdate = null;
                installing = false;
                message.error("更新失败，请稍后重试");
              } finally {
                hide();
              }
            },
          });
          destroyPrompt = prompt.destroy;
        })
        .catch(() => {
          setDesktopUpdatePromptActive(false);
          // 启动检查失败不打断主应用；下一次启动会再次检查。
        });
    }, updateCheckDelayMs);

    return () => {
      active = false;
      controller.abort();
      window.clearTimeout(timer);
      destroyPrompt?.();
      setDesktopUpdatePromptActive(false);
      if (!installing) void pendingUpdate?.close();
    };
  }, [message, modal]);

  return null;
}
